import React, { useRef, useState } from "react";
import { Plus } from "lucide-react";
import { REACT_EMOJIS, EMOJIS } from "./emoji";
import { variantsOf, LONG_PRESS_MS, readSkinTone, writeSkinTone, skinToneOf, hasSkinTones, withSkinTone } from "./emojiVariants";
import { hapticLight } from "@/lib/haptics";

// Barre de réactions d'une bulle (ouverte par appui long / bouton « Réagir »).
// Lot 730 — garder le doigt sur un emoji → fenêtre : ses variantes (teinte / famille) + « En grand » (envoie l'emoji
// en message géant, façon Messenger). Un tap simple réagit comme avant.
export const ReactionBar = ({ m, mine, t, onReact, onSendBig, showAll, setShowAll }) => {
  const [variantFor, setVariantFor] = useState(null);
  const [tone, setTone] = useState(readSkinTone);  // lot 731 — teinte mémorisée (🙏 👍 de la barre rapide)
  const timer = useRef(null);
  const fired = useRef(false);

  const pick = (e) => {
    setShowAll(false); setVariantFor(null);
    if (hasSkinTones(e)) { const tn = skinToneOf(e); writeSkinTone(tn); setTone(tn); }  // teinte choisie (ou base = réinitialisée) → mémorisée
    onReact(m.id, e);
  };
  const pressStart = (e) => {
    fired.current = false;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { fired.current = true; setShowAll(false); setVariantFor(e); hapticLight(); }, LONG_PRESS_MS);
  };
  const pressEnd = () => clearTimeout(timer.current);
  const tap = (e) => { if (fired.current) { fired.current = false; return; } pick(e); };

  return (
    <>
      <div className={`absolute z-[81] -top-11 ${mine ? "right-0" : "left-0"} flex items-center gap-1 rounded-full shadow-lg border px-2 py-1.5 bg-white select-none`} style={{ borderColor: "var(--yalola-border)", touchAction: "manipulation" }} data-testid={`reaction-bar-${m.id}`} onContextMenu={(e) => e.preventDefault()}>
        {REACT_EMOJIS.map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => tap(withSkinTone(e, tone))}
            onPointerDown={() => pressStart(e)}
            onPointerUp={pressEnd}
            onPointerLeave={pressEnd}
            onPointerCancel={pressEnd}
            className={`text-xl leading-none hover:scale-125 transition-transform rounded-full ${variantFor === e ? "scale-125" : ""}`}
            data-testid={`react-${m.id}-${e}`}
          >
            {withSkinTone(e, tone)}
          </button>
        ))}
        <button type="button" onClick={() => { setVariantFor(null); setShowAll((v) => !v); }} className="h-7 w-7 grid place-items-center rounded-full ml-0.5 transition-transform active:scale-90" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} data-testid={`react-more-${m.id}`} aria-label={t("chat.more_emojis", "Plus d'emojis")}><Plus size={16} /></button>
      </div>
      {variantFor && (
        <>
          <div className="fixed inset-0 z-[82]" onClick={() => setVariantFor(null)} data-testid="reaction-variants-backdrop" />
          <div className={`absolute z-[83] -top-[6.25rem] ${mine ? "right-0" : "left-0"} rounded-2xl border shadow-2xl bg-white p-1.5 flex items-center gap-1 chat-react-pop select-none`} style={{ borderColor: "var(--yalola-border)", transformOrigin: mine ? "right bottom" : "left bottom" }} data-testid={`react-variants-${m.id}`} onContextMenu={(e) => e.preventDefault()}>
            {variantsOf(variantFor).map((v) => (
              <button key={v} type="button" onClick={() => pick(v)} className={`text-2xl leading-none h-9 w-9 grid place-items-center rounded-xl hover:bg-[color:var(--yalola-surface-alt)] hover:scale-110 transition-transform ${hasSkinTones(v) && skinToneOf(v) === tone ? "ring-2 ring-[color:var(--yalola-primary)]" : ""}`} data-testid={`react-variant-${m.id}-${v}`} aria-current={hasSkinTones(v) && skinToneOf(v) === tone ? "true" : undefined}>{v}</button>
            ))}
            <div className="w-px self-stretch mx-0.5" style={{ background: "var(--yalola-border)" }} />
            <button type="button" onClick={() => { setVariantFor(null); onSendBig(withSkinTone(variantFor, tone)); }} className="h-9 px-2 rounded-xl flex items-center gap-1 hover:bg-[color:var(--yalola-surface-alt)] transition-transform active:scale-95" data-testid={`react-big-${m.id}`}>
              <span className="text-2xl leading-none">{withSkinTone(variantFor, tone)}</span>
              <span className="text-[11px] font-bold leading-tight text-left" style={{ color: "var(--yalola-text)" }}>{t("chat.react_big", "En grand")}</span>
            </button>
          </div>
        </>
      )}
      {showAll && (
        <>
          <div className="fixed inset-0 z-[82]" onClick={() => setShowAll(false)} data-testid="reaction-emoji-backdrop" />
          <div className="fixed left-1/2 -translate-x-1/2 bottom-24 z-[83] w-[min(340px,92vw)] max-h-56 overflow-y-auto rounded-2xl border shadow-2xl bg-white p-2" style={{ borderColor: "var(--yalola-border)" }} data-testid={`react-emoji-panel-${m.id}`}>
            <div className="grid grid-cols-8 gap-1">
              {EMOJIS.map((e) => (
                <button key={e} type="button" onClick={() => pick(e)} className="text-xl leading-none py-1 rounded-lg hover:bg-[color:var(--yalola-surface-alt)] hover:scale-110 transition-transform" data-testid={`react-all-${m.id}-${e}`}>{e}</button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
};
